import log from '../utility/debug.js'

import { useRef, useMemo, useCallback } from 'react'

export default function useItemKeys({ getItemId }) {
	// These "listeners" will be called in case the auto-generated item keys are reset
	// due to the prefix counter number reaching its maximum allowed value.
	const itemKeysResetEventListeners = useMemo(() => {
		return []
	}, [])

	// Adds an "on item keys reset" listener.
	const onItemKeysReset = useCallback((listener) => {
		itemKeysResetEventListeners.push(listener)
	}, [])

	// List items are rendered with `key`s so that React doesn't
	// "reuse" `itemComponent`s in cases when `items` are changed.
  const itemKeyPrefix = useRef()

	// When no `getItemId()` function is specified, it creates an item key
	// from an auto-generated unique prefix and the item's index in the `items` array.
	// This way, when the `items` change, their keys are also changed.
	const generateItemKeyPrefix = useMemo(() => {
		let counter = 0
		function getNextCounter() {
			if (counter === Number.MAX_SAFE_INTEGER) {
				for (const listener of itemKeysResetEventListeners) {
					listener()
				}
				counter = 0
			}
			counter++
			return counter
		}
		return () => {
			itemKeyPrefix.current = String(getNextCounter())
		}
	}, [
		itemKeyPrefix,
		itemKeysResetEventListeners
	])

	useMemo(() => {
		// Generate an initial unique `key` prefix for list items.
		generateItemKeyPrefix()
	}, [])

	// If `getItemId()` function is defined, then item `id`s are gonna be the item element `key`s.
	const usesAutogeneratedItemKeys = !getItemId

	const generateItemKeyPrefixIfNotUsingItemIds = useCallback(() => {
		if (usesAutogeneratedItemKeys) {
			generateItemKeyPrefix()
			log('React: ~ Item key prefix:', itemKeyPrefix.current)
		}
	}, [
		usesAutogeneratedItemKeys,
		generateItemKeyPrefix
	])

	/**
	 * Returns a `key` for an `item`'s element.
	 * @param  {object} item — The item.
	 * @param  {number} i — Item's index in `items` list.
	 * @return {any}
	 */
	const getItemKey = useCallback((item, i) => {
		if (getItemId) {
			return String(getItemId(item))
		}
		return `${itemKeyPrefix.current}:${i}`
	}, [
		getItemId,
		itemKeyPrefix
	])

	return {
		getItemKey,
		onItemKeysReset,
		usesAutogeneratedItemKeys,
		updateItemKeysForNewItems: generateItemKeyPrefixIfNotUsingItemIds
	}
}